import React, { useMemo } from 'react';
import SurveyForm from '../../SurveyForm';

const DEFAULT_MAX_AGE = 18;

function buildAgeOptions(max = DEFAULT_MAX_AGE) {
  const opts = ['<1'];
  for (let i = 1; i <= max; i++) opts.push(String(i));
  opts.push(`${max}+`);
  return opts;
}

function buildChildrenCountOptions(max = 10) {
  const opts = [];
  for (let i = 0; i <= max; i++) opts.push(String(i));
  opts.push(`${max}+`);
  return opts;
}

function ageToNumber(v, maxAge) {
  if (!v) return null;
  if (v === '<1') return 0;
  if (v.endsWith('+')) return maxAge + 1;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export default function PreSurvey({
  onSubmit,
  maxAge = DEFAULT_MAX_AGE,
  maxChildren = 10,
}) {
  const ageOptions = useMemo(
    () => buildAgeOptions(maxAge).map((v) => ({ value: v, label: v })),
    [maxAge]
  );

  const childrenOptions = useMemo(
    () =>
      buildChildrenCountOptions(maxChildren).map((v) => ({
        value: v,
        label: v,
      })),
    [maxChildren]
  );

  const questions = useMemo(
    () => [
      {
        name: 'childrenCount',
        type: 'select',
        label: 'How many children live in your household?',
        placeholder: 'Select…',
        options: childrenOptions,
      },
      {
        name: 'youngestAge',
        type: 'select',
        label: 'What is the age of your youngest child?',
        hint: 'In years',
        placeholder: 'Select…',
        options: ageOptions,
      },
      {
        name: 'oldestAge',
        type: 'select',
        label: 'What is the age of your oldest child?',
        hint: 'In years',
        placeholder: 'Select…',
        options: ageOptions,
      },
      {
        name: 'zip',
        type: 'zip',
        label: 'What is your ZIP code?',
        hint: '5 digits',
        placeholder: 'e.g., 80202',
      },

      // Form-level validation hook (optional, only triggers if both ages are set)
      {
        name: 'ageOrder',
        type: 'text',
        label: '', // hidden pseudo-field, used only for validation target
        validateForm: (values) => {
          const y = ageToNumber(values.youngestAge, maxAge);
          const o = ageToNumber(values.oldestAge, maxAge);
          if (y === null || o === null) return null;
          if (y > o) {
            return {
              name: 'oldestAge',
              message:
                'Youngest child age cannot be greater than oldest child age.',
            };
          }
          return null;
        },
      },
    ],
    [ageOptions, childrenOptions, maxAge]
  );

  // Remove the "ageOrder" pseudo question from rendering:
  const visibleQuestions = useMemo(
    () => questions.filter((q) => q.name !== 'ageOrder'),
    [questions]
  );

  return (
    <SurveyForm
      title='Welcome to ezParent!'
      description='To help us better understand who’s using the program, we have a few quick questions for you. These questions are completely optional, and you may skip any that you prefer not to answer.'
      questions={visibleQuestions}
      onSubmit={onSubmit}
      defaultValues={{
        childrenCount: '',
        youngestAge: '',
        oldestAge: '',
        zip: '',
      }}
    />
  );
}
